import { Channel } from '@/Enums/Channel.enum';
import { CHANNELS, GAM_CHANNELS, isGamChannel } from '@/constants/forecaster';
import { MAP_SCALES } from '@/constants/geo';
import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectHasGamAccount, selectVisibleForecasterChannels } from './currentWhitelabel.selectors';

export const selectForecaster = (state: RootState) => state.forecaster;

export const selectForecasterChannelIds = createSelector(
  [selectVisibleForecasterChannels, selectHasGamAccount],
  (forecasterChannelsVisibleToWhitelabel, hasGamAccount) =>
    Object.values(Channel).filter(channel => {
      const isVisibleToWhitelabel = forecasterChannelsVisibleToWhitelabel.some(vc => vc.name === channel);

      return GAM_CHANNELS.includes(channel) ? hasGamAccount && isVisibleToWhitelabel : isVisibleToWhitelabel;
    }),
);

export const selectForecasterChannels = createSelector(
  [selectVisibleForecasterChannels, selectHasGamAccount],
  (forecasterChannelsVisibleToWhitelabel, hasGamAccount) =>
    CHANNELS.filter(channel => {
      const isVisibleToWhitelabel = forecasterChannelsVisibleToWhitelabel.some(vc => vc.name === channel.id);

      return channel.requiresGamAccount ? hasGamAccount && isVisibleToWhitelabel : isVisibleToWhitelabel;
    }),
);

export const selectForecasterAudiences = createSelector([selectForecaster], ({ audience }) => audience);

export const selectForecastGeoImpressions = createSelector([selectForecaster], ({ geoImpressions }) => geoImpressions);

export const selectForecastGeoOptions = (channel: Channel) =>
  createSelector([selectForecaster], ({ geoOptions, geoOptionsRequestId }) => {
    if (channel === Channel.Dooh) {
      return {
        geoOptions: geoOptions.filter((geoOption: { type: string }) => geoOption.type === MAP_SCALES.ZIP),
        geoOptionsRequestId,
      };
    }

    if (isGamChannel(channel)) {
      const GAM_GEO_TYPES: string[] = [MAP_SCALES.STATE, MAP_SCALES.CITY, MAP_SCALES.COUNTY, MAP_SCALES.DMA];
      return {
        geoOptions: geoOptions.filter((geoOption: { type: string }) => GAM_GEO_TYPES.includes(geoOption.type)),
        geoOptionsRequestId,
      };
    }

    return {
      geoOptions,
      geoOptionsRequestId,
    };
  });

export const selectForecasterProducts = createSelector([selectForecaster], ({ products }) => products);
